import Vue from 'vue'
import directChatService from '@/services/directChatService'

/**
 * Estado de cada conversación individual:
 * {
 *   id: string,               // UUID del chat directo (puede ser null si aún no se creó en API)
 *   localId: string,          // ID local temporal para gestión UI
 *   title: string,            // Nombre del residente
 *   avatar: string|null,      // URL del avatar del residente
 *   idResident: string|number,
 *   messages: Array,          // Lista de mensajes
 *   unreadCount: number,
 *   lastMessage: string|null,
 *   lastMessageAt: string|null,
 *   isMinimized: boolean,
 *   isLoading: boolean,
 *   hasMore: boolean,         // Si hay más páginas de mensajes
 *   currentPage: number,
 *   isNew: boolean            // badge de animación para nuevo mensaje
 * }
 */

const state = {
  chats: [], // Array de objetos de conversación
  isInitialized: false // Si ya se cargaron los chats del servidor
}

const getters = {
  allChats: state => state.chats,
  activeChats: state => state.chats.filter(c => !c.isMinimized),
  minimizedChats: state => state.chats.filter(c => c.isMinimized),
  totalUnread: state => state.chats.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
  getChatById: state => id => state.chats.find(c => c.id === id || c.localId === id),
  isInitialized: state => state.isInitialized
}

const actions = {
  /**
   * Carga los chats existentes desde el servidor al iniciar sesión
   */
  async initDirectChats ({ commit, state }) {
    if (state.isInitialized) return
    try {
      const serverChats = await directChatService.getDirectChats()
      commit('SET_CHATS_FROM_SERVER', serverChats)
      commit('SET_INITIALIZED', true)
    } catch (error) {
      console.error('[DirectChats] Error cargando chats:', error)
    }
  },

  /**
   * Abre o crea un chat cuando se recibe el evento start_chat por WebSocket
   * @param {object} wsMessage - Mensaje del WebSocket con { idResident, residentName, residentAvatar, idDirectChat }
   */
  async openChatFromWebSocket ({ commit, state, dispatch, rootGetters }, wsMessage) {
    const { idResident, residentName, residentAvatar, idDirectChat } = wsMessage

    // Verificar si ya existe un chat con ese residente
    const existing = state.chats.find(c =>
      (c.idResident === idResident) || (idDirectChat && c.id === idDirectChat)
    )

    if (existing) {
      // Si existe y está minimizado, expandirlo
      if (existing.isMinimized) {
        commit('SET_CHAT_MINIMIZED', { chatId: existing.localId, minimized: false })
      }
      // Marcar como nuevo para animación
      commit('MARK_CHAT_NEW', { chatId: existing.localId })
      return
    }

    // Crear nuevo chat local
    const localId = `local_${Date.now()}_${idResident}`
    const newChat = {
      id: idDirectChat || null,
      localId,
      title: residentName || `Residente #${idResident}`,
      avatar: residentAvatar || null,
      idResident,
      messages: [],
      unreadCount: 1,
      lastMessage: null,
      lastMessageAt: new Date().toISOString(),
      isMinimized: false,
      isLoading: false,
      hasMore: false,
      currentPage: 1,
      isNew: true
    }

    commit('ADD_CHAT', newChat)

    // Si llegó el idDirectChat del WS, cargar mensajes previos
    if (idDirectChat) {
      dispatch('loadMessages', { chatLocalId: localId, chatId: idDirectChat })
    } else {
      // Intentar crear el chat en el API usando idVigilant del perfil
      try {
        const profile = rootGetters['getProfile']
        if (profile && profile.idVigilant) {
          const created = await directChatService.createDirectChat(profile.idVigilant, idResident)
          commit('SET_CHAT_SERVER_ID', { localId, serverId: created.id })
          dispatch('loadMessages', { chatLocalId: localId, chatId: created.id })
        }
      } catch (error) {
        console.error('[DirectChats] Error creando chat:', error)
      }
    }
  },

  /**
   * Carga los mensajes de un chat (primera página)
   */
  async loadMessages ({ commit }, { chatLocalId, chatId }) {
    if (!chatId) return
    commit('SET_CHAT_LOADING', { chatLocalId, loading: true })
    try {
      const data = await directChatService.getDirectMessages(chatId, 1, 20)
      // La API devuelve más reciente → más antiguo, invertir para mostrar cronológico
      const messages = [...(data.results || [])].reverse()
      commit('SET_CHAT_MESSAGES', { chatLocalId, messages })
      commit('SET_CHAT_HAS_MORE', { chatLocalId, hasMore: !!data.next })
      if (messages.length > 0) {
        const last = messages[messages.length - 1]
        commit('SET_LAST_MESSAGE', {
          chatLocalId,
          content: last.content,
          at: last.createdAt
        })
      }
    } catch (error) {
      console.error('[DirectChats] Error cargando mensajes:', error)
    } finally {
      commit('SET_CHAT_LOADING', { chatLocalId, loading: false })
    }
  },

  /**
   * Carga más mensajes (paginación hacia atrás)
   */
  async loadMoreMessages ({ commit, getters }, chatLocalId) {
    const chat = getters.getChatById(chatLocalId)
    if (!chat || !chat.id || !chat.hasMore || chat.isLoading) return
    const nextPage = (chat.currentPage || 1) + 1
    commit('SET_CHAT_LOADING', { chatLocalId, loading: true })
    try {
      const data = await directChatService.getDirectMessages(chat.id, nextPage, 20)
      const newMessages = [...(data.results || [])].reverse()
      commit('PREPEND_MESSAGES', { chatLocalId, messages: newMessages })
      commit('SET_CHAT_HAS_MORE', { chatLocalId, hasMore: !!data.next })
      commit('INCREMENT_PAGE', { chatLocalId })
    } catch (error) {
      console.error('[DirectChats] Error cargando más mensajes:', error)
    } finally {
      commit('SET_CHAT_LOADING', { chatLocalId, loading: false })
    }
  },

  /**
   * Envía un mensaje
   */
  async sendMessage ({ commit, getters }, { chatLocalId, content }) {
    const chat = getters.getChatById(chatLocalId)
    if (!chat || !chat.id || !content.trim()) return

    const optimisticMsg = {
      id: `optimistic_${Date.now()}`,
      idDirectChat: chat.id,
      content: content.trim(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isOptimistic: true,
      senderData: null
    }

    commit('APPEND_MESSAGE', { chatLocalId, message: optimisticMsg })
    commit('SET_LAST_MESSAGE', { chatLocalId, content: content.trim(), at: optimisticMsg.createdAt })

    try {
      const sent = await directChatService.sendDirectMessage(chat.id, content.trim())
      commit('REPLACE_OPTIMISTIC_MESSAGE', { chatLocalId, optimisticId: optimisticMsg.id, message: sent })
    } catch (error) {
      console.error('[DirectChats] Error enviando mensaje:', error)
      commit('REMOVE_MESSAGE', { chatLocalId, messageId: optimisticMsg.id })
    }
  },

  /**
   * Maneja un nuevo mensaje entrante por WebSocket ('message_chat')
   */
  async receiveMessageFromWebSocket ({ commit, state, dispatch, rootGetters }, wsMessage) {
    const { idResident, address, idDirectChat } = wsMessage

    // Verificar si el chat ya está en la vista local
    let chat = state.chats.find(c => c.id === idDirectChat || c.idResident === idResident)

    if (!chat) {
      // Si el chat no estaba en la lista (e.g. lo había cerrado el vigilante pero no está en la bd como cerrado o es nuevo),
      // creamos la vista local usando el helper de abrir chat.
      await dispatch('openChatFromWebSocket', {
        idResident,
        residentName: address,
        idDirectChat
      })
      // Después de abrirlo, lo buscamos nuevamente.
      chat = state.chats.find(c => c.id === idDirectChat || c.idResident === idResident)
    }

    if (chat) {
      // Si está minimizado, lo abrimos automáticamente.
      if (chat.isMinimized) {
        commit('SET_CHAT_MINIMIZED', { chatId: chat.localId, minimized: false })
      }

      // Como no tenemos el contenido del mensaje completo en el payload de `message_chat`
      // (según la doc, el WS manda la notificación de que *hay* un mensaje, pero no su contenido real),
      // recargamos los mensajes para obtener el último o la lista actualizada.
      await dispatch('loadMessages', { chatLocalId: chat.localId, chatId: idDirectChat })

      // Animación de nuevo mensaje
      commit('MARK_CHAT_NEW', { chatId: chat.localId })
    }
  },

  /**
   * Cierra/elimina un chat de la lista local
   */
  closeChat ({ commit }, chatLocalId) {
    commit('REMOVE_CHAT', chatLocalId)
  },

  /**
   * Minimiza o expande un chat
   */
  toggleMinimize ({ commit, getters }, chatLocalId) {
    const chat = getters.getChatById(chatLocalId)
    if (!chat) return
    commit('SET_CHAT_MINIMIZED', { chatId: chatLocalId, minimized: !chat.isMinimized })
    if (!chat.isMinimized) {
      // Al minimizar, limpiar badge de nuevo
      commit('CLEAR_CHAT_NEW', { chatId: chatLocalId })
    } else {
      // Al expandir, marcar como leído
      commit('MARK_AS_READ', chatLocalId)
    }
  },

  /**
   * Marca los mensajes de un chat como leídos
   */
  markAsRead ({ commit }, chatLocalId) {
    commit('MARK_AS_READ', chatLocalId)
  }
}

const mutations = {
  SET_INITIALIZED (state, value) {
    state.isInitialized = value
  },

  SET_CHATS_FROM_SERVER (state, serverChats) {
    // Mapear los chats del servidor al formato local
    const localChats = serverChats.map(sc => ({
      id: sc.id,
      localId: `server_${sc.id}`,
      title: sc.address || `Residente #${sc.idResident}`,
      avatar: sc.residentAvatar || null,
      idResident: sc.idResident,
      messages: [],
      unreadCount: 0,
      lastMessage: sc.lastMessage || null,
      lastMessageAt: sc.updatedAt || sc.createdAt,
      isMinimized: true, // Los chats del servidor empiezan minimizados
      isLoading: false,
      hasMore: false,
      currentPage: 1,
      isNew: false
    }))
    Vue.set(state, 'chats', localChats)
  },

  ADD_CHAT (state, chat) {
    state.chats.push(chat)
  },

  REMOVE_CHAT (state, chatLocalId) {
    const idx = state.chats.findIndex(c => c.localId === chatLocalId || c.id === chatLocalId)
    if (idx !== -1) state.chats.splice(idx, 1)
  },

  SET_CHAT_SERVER_ID (state, { localId, serverId }) {
    const chat = state.chats.find(c => c.localId === localId)
    if (chat) Vue.set(chat, 'id', serverId)
  },

  SET_CHAT_MINIMIZED (state, { chatId, minimized }) {
    const chat = state.chats.find(c => c.localId === chatId || c.id === chatId)
    if (chat) Vue.set(chat, 'isMinimized', minimized)
  },

  SET_CHAT_LOADING (state, { chatLocalId, loading }) {
    const chat = state.chats.find(c => c.localId === chatLocalId || c.id === chatLocalId)
    if (chat) Vue.set(chat, 'isLoading', loading)
  },

  SET_CHAT_MESSAGES (state, { chatLocalId, messages }) {
    const chat = state.chats.find(c => c.localId === chatLocalId || c.id === chatLocalId)
    if (chat) Vue.set(chat, 'messages', messages)
  },

  PREPEND_MESSAGES (state, { chatLocalId, messages }) {
    const chat = state.chats.find(c => c.localId === chatLocalId)
    if (chat) Vue.set(chat, 'messages', [...messages, ...chat.messages])
  },

  APPEND_MESSAGE (state, { chatLocalId, message }) {
    const chat = state.chats.find(c => c.localId === chatLocalId || c.id === chatLocalId)
    if (chat) chat.messages.push(message)
  },

  REPLACE_OPTIMISTIC_MESSAGE (state, { chatLocalId, optimisticId, message }) {
    const chat = state.chats.find(c => c.localId === chatLocalId)
    if (chat) {
      const idx = chat.messages.findIndex(m => m.id === optimisticId)
      if (idx !== -1) Vue.set(chat.messages, idx, message)
    }
  },

  REMOVE_MESSAGE (state, { chatLocalId, messageId }) {
    const chat = state.chats.find(c => c.localId === chatLocalId)
    if (chat) {
      const idx = chat.messages.findIndex(m => m.id === messageId)
      if (idx !== -1) chat.messages.splice(idx, 1)
    }
  },

  SET_LAST_MESSAGE (state, { chatLocalId, content, at }) {
    const chat = state.chats.find(c => c.localId === chatLocalId || c.id === chatLocalId)
    if (chat) {
      Vue.set(chat, 'lastMessage', content)
      Vue.set(chat, 'lastMessageAt', at)
    }
  },

  SET_CHAT_HAS_MORE (state, { chatLocalId, hasMore }) {
    const chat = state.chats.find(c => c.localId === chatLocalId)
    if (chat) Vue.set(chat, 'hasMore', hasMore)
  },

  INCREMENT_PAGE (state, { chatLocalId }) {
    const chat = state.chats.find(c => c.localId === chatLocalId)
    if (chat) Vue.set(chat, 'currentPage', (chat.currentPage || 1) + 1)
  },

  INCREMENT_UNREAD (state, { chatLocalId }) {
    const chat = state.chats.find(c => c.localId === chatLocalId || c.id === chatLocalId)
    if (chat) Vue.set(chat, 'unreadCount', (chat.unreadCount || 0) + 1)
  },

  MARK_AS_READ (state, chatLocalId) {
    const chat = state.chats.find(c => c.localId === chatLocalId || c.id === chatLocalId)
    if (chat) Vue.set(chat, 'unreadCount', 0)
  },

  MARK_CHAT_NEW (state, { chatId }) {
    const chat = state.chats.find(c => c.localId === chatId || c.id === chatId)
    if (chat) Vue.set(chat, 'isNew', true)
  },

  CLEAR_CHAT_NEW (state, { chatId }) {
    const chat = state.chats.find(c => c.localId === chatId || c.id === chatId)
    if (chat) Vue.set(chat, 'isNew', false)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
